import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { Amplify, Auth } from 'aws-amplify';
import { currentConfig } from '../../amplify-config';
import api from '../../services/DataLayer';

Amplify.configure(currentConfig);

export const AuthStatus = Object.freeze({
  INITIAL: 'initial',
  IN_PROGRESS: 'inProgress',
  CHANGE_PW: 'changePW',
  SIGN_IN: 'signIn',
  AUTHENTICATED: 'authenticated',
});

export const AuthError = Object.freeze({
  NONE: 'none',
  LOGIN_FAILED: 'loginFailed',
  LOGOUT_FAILED: 'logoutFailed',
});

export const UserRoles = Object.freeze({
  USER: 'user',
  ADMIN: 'admin',
  list() {
    return Object.values(this).slice(0, -1);
  },
});

const initialState = {
  authStatus: AuthStatus.INITIAL,
  authError: false,
  authErrorType: AuthError.NONE,
  user: undefined,
  userId: undefined,
  roles: [],
  isSuperUser: false,
  forceLogin: false,
  refreshing: false,
};

const fetchUserRoles = async (userId) => {
  const roles = await api.getUserRoles(userId);
  return roles;
};

export const cookieSignIn = createAsyncThunk('authentication/cookieSignIn', async (navigate) => {
  try {
    const user = await Auth.currentSession();
    const roles = await fetchUserRoles(user.accessToken.payload.sub);
    const groups = user.accessToken.payload['cognito:groups'] ?? [];
    const expiry = user.accessToken.payload.exp;
    navigate('/selection');
    return { email: user.idToken.payload.email, isSuperUser: groups.includes('superuser'), expires: expiry, roles };
  } catch (error) {
    navigate('/');
    return undefined;
  }
});

export const signIn = createAsyncThunk('authentication/signIn', async (loginInfo) => {
  try {
    const user = await Auth.signIn(loginInfo.username, loginInfo.password);
    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      return { email: user.challengeParam.userAttributes.email, challengeName: user.challengeName, userId: user.username };
    }
    const roles = await fetchUserRoles(user.attributes.sub);
    const groups = user.signInUserSession.accessToken.payload['cognito:groups'] ?? [];
    const expiry = user.signInUserSession.accessToken.payload.exp;
    return { email: user.attributes.email, isSuperUser: groups.includes('superuser'), expires: expiry, roles };
  } catch (error) {
    console.log(error);
    return undefined;
  }
});

const saveChanges = async (id, frameworks, assessments) => {
  const unsaved = [];
  if (frameworks.unsavedChanges) {
    unsaved.push(api.updateCustomFramework(
      id,
      frameworks.selectedFrameworkId,
      frameworks.customFrameworks.find((fw) => fw.id === frameworks.selectedFrameworkId),
    ));
  }
  if (assessments.unsavedChanges) {
    unsaved.push(api.updateOrganizationAssessment(
      id,
      assessments.selectedAssessment,
    ));
  }
  const _ = await Promise.all(unsaved);
  return true;
};

export const signOut = createAsyncThunk('authentication/signOut', async (_, { getState, dispatch }) => {
  try {
    const { frameworks, assessments } = getState();
    const { id } = getState().organizations.selectedOrganization;
    await saveChanges(id, frameworks, assessments);
    const _ = await Auth.signOut();
    dispatch({ type: 'resetState' });
    return true;
  } catch (error) {
    console.log(error);
    return undefined;
  }
});

export const saveUnsaved = createAsyncThunk('authentication/saveUnsaved', async (_, { getState, dispatch }) => {
  try {
    const unsaved = [];
    const { frameworks, assessments } = getState();
    const { id } = getState().organizations.selectedOrganization;
    if (frameworks.unsavedChanges) {
      unsaved.push(api.updateCustomFramework(
        id,
        frameworks.selectedFrameworkId,
        frameworks.customFrameworks.find((fw) => fw.id === frameworks.selectedFrameworkId),
      ));
    }
    if (assessments.unsavedChanges) {
      unsaved.push(api.updateOrganizationAssessment(
        id,
        assessments.selectedAssessment,
      ));
    }
    const _ = await Promise.all(unsaved);
    dispatch({ type: 'resetState' });
    return true;
  } catch (error) {
    console.log(error);
    return undefined;
  }
});

export const setPasswordAndProfile = createAsyncThunk('authentication/setPasswordAndProfile', async ({ loginInfo, userProfile }) => {
  try {
    const user = await Auth.signIn(loginInfo.username, loginInfo.previousPassword);
    const completedUser = await Auth.completeNewPassword(user, loginInfo.password);
    const userId = completedUser.signInUserSession.accessToken.payload.sub;
    const requests = [
      fetchUserRoles(userId),
      api.updateUserProfile(userId, { ...userProfile, email: loginInfo.username }),
    ];
    const responses = await Promise.all(requests);
    const roles = responses[0];
    const groups = completedUser.signInUserSession.accessToken.payload['cognito:groups'] ?? [];
    const expiry = completedUser.signInUserSession.accessToken.payload.exp;
    return { email: loginInfo.username, isSuperUser: groups.includes('superuser'), expires: expiry, roles };
  } catch (error) {
    console.log(error);
    return undefined;
  }
});

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    forceLogin: (state, _) => {
      state.authStatus = AuthStatus.SIGN_IN;
      state.refreshing = true;
      state.loginExpires = undefined;
    },
    refresh: (state, action) => {
      state.loginExpires = action.payload.loginExpires;
      state.refreshing = action.payload.refreshing ?? state.refreshing;
      state.expires = action.payload.expires ?? state.expires;
    },
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },
    setAuthStatus: (state, action) => {
      state.authStatus = action.payload;
    },
    resetAuthError: (state, _) => {
      state.authError = false;
      state.authErrorType = AuthError.NONE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cookieSignIn.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.user = action.payload.email;
          state.authStatus = AuthStatus.AUTHENTICATED;
          state.isSuperUser = action.payload.isSuperUser;
          state.roles = action.payload.roles;
          state.refreshing = false;
          state.expires = action.payload.expires;
        } else {
          state.authStatus = AuthStatus.SIGN_IN;
        }
      })
      .addCase(setPasswordAndProfile.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.user = action.payload.email;
          state.userId = undefined;
          state.isSuperUser = action.payload.isSuperUser;
          state.roles = action.payload.roles;
          state.refreshing = false;
          state.expires = action.payload.expires;
          state.authStatus = AuthStatus.AUTHENTICATED;
        } else {
          state.authError = true;
          state.authErrorType = AuthError.LOGIN_FAILED;
          state.authStatus = AuthStatus.CHANGE_PW;
        }
      })
      .addCase(signIn.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          if (action.payload.challengeName === undefined) {
            state.user = action.payload.email;
            state.authStatus = AuthStatus.AUTHENTICATED;
            state.isSuperUser = action.payload.isSuperUser;
            state.roles = action.payload.roles;
            state.refreshing = false;
            state.expires = action.payload.expires;
          } else {
            state.user = action.payload.email;
            if (action.payload.challengeName === 'NEW_PASSWORD_REQUIRED') {
              state.authStatus = AuthStatus.CHANGE_PW;
            }
          }
        } else {
          state.authError = true;
          state.authErrorType = AuthError.LOGIN_FAILED;
          state.authStatus = AuthStatus.SIGN_IN;
        }
      })
      .addCase(signOut.fulfilled, (state, action) => {
        const newState = action.payload === undefined
          ? {
            ...state,
            authError: true,
            authErrorType: AuthError.LOGIN_FAILED,
          }
          : {
            ...initialState,
            authStatus: AuthStatus.SIGN_IN,
          };
        return newState;
      });
  },
});

const { actions, reducer } = authenticationSlice;

export default reducer;

export const { forceLogin, refresh, setRefreshing, setAuthStatus, resetAuthError } = actions;

const selectError = (state) => state.authentication.authError;
const selectErrorType = (state) => state.authentication.authErrorType;

export const selectAuthError = createSelector(selectError, selectErrorType, (error, type) => ({
  authError: error,
  authErrorType: type,
}));
export const selectAuthStatus = (state) => state.authentication.authStatus;
export const selectAuthenticated = createSelector(selectAuthStatus, (status) => status === AuthStatus.AUTHENTICATED);
export const selectIsSuperUser = (state) => state.authentication.isSuperUser;
export const selectRoles = (state) => state.authentication.roles;
export const selectOrganizationsWithRole = createSelector(
  selectRoles,
  (roles) => roles.map((role) => role.organizationId),
);
export const selectUserId = (state) => state.authentication.userId;